/**
 * Short Authentication String (SAS) derivation.
 *
 * The SAS is a strong (>=80-bit) public function of the finalized Fume
 * transcript hash `T.hash`. A weak 6-digit decimal SAS is explicitly
 * prohibited; we emit 80 bits as a 20-character uppercase hex string so humans
 * compare a value with negligible online-guessing advantage.
 */
import { sha256 } from './hash.js';
import { FumeCryptoError } from './errors.js';
import { SAS_DOMAIN } from './constants.js';
import { concatBytes } from './internal.js';
/** Minimum SAS strength, in bits. */
export const SAS_MIN_BITS = 80;
const TRANSCRIPT_HASH_SIZE = 32;
const SAS_BYTES = SAS_MIN_BITS / 8; // 10 bytes == 80 bits
const utf8 = new TextEncoder();
/**
 * Derive the SAS from the finalized Fume transcript hash.
 *   sasBytes = SHA-256("fume.sas.v1" || T.hash).slice(0, 10)   // 80 bits
 * Returns a 20-character uppercase hexadecimal string.
 */
export function deriveSas(transcriptHash) {
    if (!(transcriptHash instanceof Uint8Array) || transcriptHash.length !== TRANSCRIPT_HASH_SIZE) {
        throw new FumeCryptoError(`transcriptHash must be exactly ${TRANSCRIPT_HASH_SIZE} bytes`);
    }
    const digest = sha256(concatBytes(utf8.encode(SAS_DOMAIN), transcriptHash));
    return Buffer.from(digest.slice(0, SAS_BYTES)).toString('hex').toUpperCase();
}
//# sourceMappingURL=sas.js.map